<script lang="ts">
  import { createEventDispatcher, onDestroy } from 'svelte';
  import L from 'leaflet';
  import { polygonStore, type PolygonVertex } from '../lib/stores/polygon';

  const dispatch = createEventDispatcher<{ cancel: void }>();

  /** The Leaflet map instance to draw on. */
  export let map: any;

  /** When true, click handlers are bound and vertices accumulate. When
   * false, all in-progress preview state is torn down. */
  export let active = false;

  /** Working set of vertices for the in-progress polygon. */
  let working: PolygonVertex[] = [];
  let vertexMarkers: L.CircleMarker[] = [];
  let previewLine: L.Polyline | null = null;

  // A browser double-click emits click + click + dblclick at the same spot,
  // so the closing gesture would otherwise drop a duplicate vertex on the
  // corner. Skip a click that lands on (essentially) the previous vertex.
  // ~1e-6° is a few centimetres — far below any intentional vertex spacing.
  const SAME_VERTEX_EPS = 1e-6;

  function onClick(e: { latlng: { lat: number; lng: number } }) {
    const v: PolygonVertex = { lat: e.latlng.lat, lng: e.latlng.lng };
    const last = working[working.length - 1];
    if (
      last &&
      Math.abs(last.lat - v.lat) < SAME_VERTEX_EPS &&
      Math.abs(last.lng - v.lng) < SAME_VERTEX_EPS
    ) {
      return;
    }
    working = [...working, v];
    const m = L.circleMarker([v.lat, v.lng], {
      radius: 4,
      color: 'var(--leaf-bright, #4a8a3e)',
      fillColor: 'var(--paper, #f7f3ea)',
      fillOpacity: 1,
      weight: 2,
    });
    map.addLayer(m);
    vertexMarkers = [...vertexMarkers, m];
    refreshPreviewLine();
  }

  function onDblClick(e: { latlng: { lat: number; lng: number } }) {
    if (working.length < 3) return;
    // Commit the current vertex set as the closed polygon
    polygonStore.set(working);
    teardown();
  }

  function onKey(e: KeyboardEvent) {
    if (!active) return;
    if (e.key === 'Escape') {
      teardown();
      dispatch('cancel');
    }
  }

  function refreshPreviewLine() {
    if (previewLine) {
      map.removeLayer(previewLine);
      previewLine = null;
    }
    if (working.length < 2) return;
    previewLine = L.polyline(working.map(v => [v.lat, v.lng]), {
      color: 'var(--leaf-bright, #4a8a3e)',
      weight: 2,
      dashArray: '4 4',
    });
    map.addLayer(previewLine);
  }

  function teardown() {
    working = [];
    for (const m of vertexMarkers) map.removeLayer(m);
    vertexMarkers = [];
    if (previewLine) {
      map.removeLayer(previewLine);
      previewLine = null;
    }
  }

  // Reactive: when `active` flips, bind or tear down event handlers.
  let bound = false;
  $: if (map) {
    if (active && !bound) {
      map.on('click', onClick);
      map.on('dblclick', onDblClick);
      window.addEventListener('keydown', onKey);
      // Stop the close gesture from also zooming the map in.
      map.doubleClickZoom?.disable();
      bound = true;
    } else if (!active && bound) {
      map.off('click', onClick);
      map.off('dblclick', onDblClick);
      window.removeEventListener('keydown', onKey);
      map.doubleClickZoom?.enable();
      teardown();
      bound = false;
    }
  }

  onDestroy(() => {
    if (bound && map) {
      map.off('click', onClick);
      map.off('dblclick', onDblClick);
      window.removeEventListener('keydown', onKey);
      map.doubleClickZoom?.enable();
    }
    teardown();
  });
</script>
